import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import Layout from './components/Layout'
import PipedriveLayout from './components/PipedriveLayout'
import { Toaster } from './components/Toaster'
import { UserProvider } from './contexts/UserContext'
import ErrorBoundary from './components/ErrorBoundary'
import { useKeyboardShortcuts } from './hooks/use-keyboard-shortcuts'
import { ProtectedRoute } from './components/ProtectedRoute'
import { WorkspaceHomeRoute } from './components/WorkspaceHomeRoute'
import { SessionWarning } from './components/SessionWarning'
import { CallProvider } from './components/CallProvider'
import { CallLogDialog } from './components/CallLogDialog'

// Auth Pages
import Login from './pages/Login'
import AuthCallback from './pages/AuthCallback'

// Pages
import Dashboard from './pages/Dashboard'
import Tasks from './pages/Tasks'
import { Events } from './pages/Events'
import Leads from './pages/Leads'
import LeadDetail from './pages/LeadDetail'
import Opportunities from './pages/Opportunities'
import OpportunityDetail from './pages/OpportunityDetail'
import Accounts from './pages/Accounts'
import AccountDetail from './pages/AccountDetail'
import Contacts from './pages/Contacts'
import ContactDetail from './pages/ContactDetail'
import TaskDetail from './pages/TaskDetail'
import EventDetail from './pages/EventDetail'
import Quotes from './pages/Quotes'
import QuoteDetail from './pages/QuoteDetail'
import Products from './pages/Products'
import PriceListRedirect from './pages/PriceListRedirect'
import PricebookPricesEditor from './pages/PricebookPricesEditor'
import Orders from './pages/Orders'
import Battlecards from './pages/Battlecards'
import OrderDetail from './pages/OrderDetail'
import Emails from './pages/Emails'
import Content from './pages/Content'
import ContractReviews from './pages/contract-reviews/ContractReviews'
import ContractReviewDetail from './pages/contract-reviews/ContractReviewDetail'
import Repository from './pages/Repository'
import Reports from './pages/Reports'
import Analytics from './pages/Analytics'
import WhereInTheWorld from './pages/WhereInTheWorld'
import LeadSourceAnalysis from './pages/LeadSourceAnalysis'
import SalesForecast from './pages/SalesForecast'
import ReportBuilder from './pages/ReportBuilder'
import PipelineReport from './pages/PipelineReport'
import DaysToClose from './pages/DaysToClose'
import WinLossAnalysis from './pages/WinLossAnalysis'
import TopAccounts from './pages/TopAccounts'
import BigOpportunities from './pages/BigOpportunities'
import ActivitiesCreated from './pages/ActivitiesCreated'
import MTDTeams from './pages/MTDTeams'
import Rfps from './pages/Rfps'
import RfpDetail from './pages/RfpDetail'
import ClosedWonMonthly from './pages/ClosedWonMonthly'
import MarketingReports from './pages/MarketingReports'
import MarketingFunnelReports from './pages/MarketingFunnelReports'
import ProductRevenueReport from './pages/ProductRevenueReport'
import RevenueTrendReport from './pages/RevenueTrendReport'
import StuckOpportunities from './pages/StuckOpportunities'
import ExecSalesDashboard from './pages/ExecSalesDashboard'
import Settings from './pages/Settings'
import Search from './pages/Search'
import Notifications from './pages/Notifications'
import Users from './pages/Users'
import Help from './pages/Help'
import EnhancementTracker from './pages/EnhancementTracker'
import Tickets from './pages/Tickets'
import TicketDetail from './pages/TicketDetail'
import CreateTicket from './pages/CreateTicket'
import Duplicates from './pages/Duplicates'
import SupportRequest from './pages/SupportRequest'
import MassUpload from './pages/MassUpload'
import SalesTrackersOverview from './pages/sales-trackers/Overview'
import SalesTrackersProducts from './pages/sales-trackers/Products'
import SalesTrackersReps from './pages/sales-trackers/Reps'
import SalesTrackersMySales from './pages/sales-trackers/MySales'
import SalesTrackersRepDetail from './pages/sales-trackers/RepDetail'
import SalesTrackersUpload from './pages/sales-trackers/Upload'
import SalesTrackersAttribution from './pages/sales-trackers/Attribution'
import SalesTrackersWeeklyForecast from './pages/sales-trackers/WeeklyForecast'
import SalesTrackersTargets from './pages/sales-trackers/Targets'
import SalesTrackersTransactions from './pages/sales-trackers/Transactions'

// HR Module
import EmployeesPage from './pages/hr/Employees'
import EmployeeDetailPage from './pages/hr/EmployeeDetail'
import DepartmentsPage from './pages/hr/Departments'
import OrgChartPage from './pages/hr/OrgChart'
import TimeOffPage from './pages/hr/TimeOff'
import LeaveApprovalsPage from './pages/hr/LeaveApprovals'
import CompanyDocumentsPage from './pages/hr/CompanyDocuments'
import HrHome from './pages/hr/HrHome'
import MyProfileRedirect from './pages/hr/MyProfileRedirect'
import EmployeeWorldMap from './pages/hr/EmployeeWorldMap'
import TrainingSessions from './pages/training/Sessions'
import TrainingOnboarding from './pages/training/Onboarding'
import TrainingBeta from './pages/training/Beta'
import TrainingDemos from './pages/training/Demos'
import TrainingWebinars from './pages/training/Webinars'
import TrainingOfficeHours from './pages/training/OfficeHours'
import TrainingImport from './pages/training/Import'
import { RequireHrAdmin } from './components/RequireHrAdmin'

// Pipedrive Pages
import {
  PipedriveDashboard,
  PipedriveOrganizations,
  PipedriveOrganizationDetail,
  PipedrivePeople,
  PipedrivePersonDetail,
  PipedriveDeals,
  PipedriveDealDetail,
  PipedriveActivities,
  PipedriveActivityDetail,
  PipedriveNotes,
  PipedriveNoteDetail,
  PipedriveLeads,
  PipedriveProducts,
  PipedriveFiles,
} from './pages/pipedrive'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
      // Use error boundary for server errors (5xx) to show user-friendly error UI
      throwOnError: (error: unknown) => {
        const axiosError = error as { response?: { status?: number } }
        return (axiosError?.response?.status ?? 0) >= 500
      },
    },
  },
})

/**
 * Who may reach anything under /training: every CRM type, plus Training itself.
 *
 * A single list rather than six copies. Adding a Training page by copying a route
 * and forgetting one entry is how a trainer would end up locked out of a tab they
 * are supposed to read — or, worse, how a type would be admitted by accident.
 * Server-side, middleware/auth.js confines Training accounts to /api/v1/training
 * regardless of what the router allows.
 */
const TRAINING_USER_TYPES = [
  'User',
  'Marketing',
  'Orders',
  'Quotes',
  'Support',
  'RFP',
  'Training',
] as const

/**
 * Who may reach the HR workspace.
 *
 * Everyone employed here — including Employee and BDR, whose whole login is HR — but NOT
 * Training. Those ten routes carried a bare <ProtectedRoute>, so a Training account could
 * load any HR page by typing the URL; the HR API then refused it and they got an error
 * screen. Hiding the sidebar link is not a control, so the routes say it too.
 *
 * Admin and Super Admin bypass allowedUserTypes inside ProtectedRoute, so they are not
 * listed. A user type added later is refused until named — the same fail-closed rule the
 * server uses.
 */
const HR_WORKSPACE_TYPES = [
  'User',
  'Marketing',
  'Orders',
  'Quotes',
  'Support',
  'RFP',
  'Employee',
  'BDR',
] as const

/** The five tracker worksheets, as routes. */
const TRAINING_WORKSHEET_ROUTES = [
  { path: '/training/onboarding', element: <TrainingOnboarding /> },
  { path: '/training/beta', element: <TrainingBeta /> },
  { path: '/training/demos', element: <TrainingDemos /> },
  { path: '/training/webinars', element: <TrainingWebinars /> },
  { path: '/training/office-hours', element: <TrainingOfficeHours /> },
]

// Component to enable keyboard shortcuts
function KeyboardShortcutsProvider() {
  useKeyboardShortcuts()
  return null
}

function App() {
  return (
    <UserProvider>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <CallProvider>
            <BrowserRouter>
              <KeyboardShortcutsProvider />
              <SessionWarning />
              <CallLogDialog />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Protected routes */}
                <Route
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  {/* '/' had NO guard: the CRM dashboard rendered for any
                      authenticated account, HR-only Employees included. Accounts
                      with their own workspace are redirected to it. */}
                  <Route
                    path="/"
                    element={
                      <WorkspaceHomeRoute>
                        <Dashboard />
                      </WorkspaceHomeRoute>
                    }
                  />
                  {/* Training — the only section a Training account can reach.
                      Also open to every CRM type. */}
                  <Route
                    path="/training"
                    element={
                      <ProtectedRoute allowedUserTypes={[...TRAINING_USER_TYPES]}>
                        <TrainingSessions />
                      </ProtectedRoute>
                    }
                  />
                  {/* Admin-only, so it is NOT in TRAINING_WORKSHEET_ROUTES — those are
                      open to every CRM type plus Training. */}
                  <Route
                    path="/training/import"
                    element={
                      <ProtectedRoute requireAdmin>
                        <TrainingImport />
                      </ProtectedRoute>
                    }
                  />
                  {TRAINING_WORKSHEET_ROUTES.map(({ path, element }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <ProtectedRoute allowedUserTypes={[...TRAINING_USER_TYPES]}>
                          {element}
                        </ProtectedRoute>
                      }
                    />
                  ))}
                  <Route path="/search" element={<Search />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route
                    path="/tasks"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <Tasks />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tasks/:id"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <TaskDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/tickets" element={<Tickets />} />
                  <Route path="/tickets/new" element={<CreateTicket />} />
                  <Route path="/tickets/:id" element={<TicketDetail />} />
                  <Route path="/support-request" element={<SupportRequest />} />
                  <Route
                    path="/events"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <Events />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/events/:id"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <EventDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/emails"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes']}>
                        <Emails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/content"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes']}>
                        <Content />
                      </ProtectedRoute>
                    }
                  />
                  {/*
                    Contract reviews. Guarded at the route, unlike /tickets which is unguarded
                    and relies on each page hiding its own controls. That is not a pattern worth
                    copying for commercially sensitive records.

                    The guard is coarse on purpose: it only decides who may reach the pages.
                    WHICH reviews a person can see is enforced in SQL, because a route guard
                    cannot express "the requester, the reviewer, or an admin".
                  */}
                  <Route
                    path="/contract-reviews"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Orders', 'Quotes', 'RFP']}>
                        <ContractReviews />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contract-reviews/:id"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Orders', 'Quotes', 'RFP']}>
                        <ContractReviewDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/repository"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={[
                          'User',
                          'Marketing',
                          'Orders',
                          'Quotes',
                          'RFP',
                          'Support',
                        ]}
                      >
                        <Repository />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/leads"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'Support']}
                      >
                        <Leads />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/leads/:id"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'Support']}
                      >
                        <LeadDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/opportunities"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Quotes', 'Orders', 'Support']}>
                        <Opportunities />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/opportunities/:id"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Quotes', 'Orders', 'Support']}>
                        <OpportunityDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/accounts"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={[
                          'User',
                          'Marketing',
                          'Orders',
                          'Quotes',
                          'RFP',
                          'Support',
                        ]}
                      >
                        <Accounts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/accounts/:id"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={[
                          'User',
                          'Marketing',
                          'Orders',
                          'Quotes',
                          'RFP',
                          'Support',
                        ]}
                      >
                        <AccountDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contacts"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={[
                          'User',
                          'Marketing',
                          'Orders',
                          'Quotes',
                          'RFP',
                          'Support',
                        ]}
                      >
                        <Contacts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contacts/:id"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={[
                          'User',
                          'Marketing',
                          'Orders',
                          'Quotes',
                          'RFP',
                          'Support',
                        ]}
                      >
                        <ContactDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/duplicates"
                    element={
                      <ProtectedRoute requireAdmin>
                        <Duplicates />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/quotes"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Quotes', 'Orders']}>
                        <Quotes />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/quotes/:id"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Quotes', 'Orders']}>
                        <QuoteDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/products"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Orders', 'Quotes', 'RFP']}>
                        <Products />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pricebooks"
                    element={
                      <ProtectedRoute requireAdmin>
                        <PriceListRedirect />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pricebooks/:id/prices"
                    element={
                      <ProtectedRoute requireAdmin>
                        <PricebookPricesEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Orders']}>
                        <Orders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders/new"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Orders']}>
                        <OrderDetail isNew />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders/:id"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Orders']}>
                        <OrderDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/battlecards"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'Orders']}>
                        <Battlecards />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <Reports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/builder"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <ReportBuilder />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/pipeline"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <PipelineReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/days-to-close"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <DaysToClose />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/win-loss-analysis"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <WinLossAnalysis />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/top-accounts"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <TopAccounts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/big-opportunities"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <BigOpportunities />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/activities-created"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <ActivitiesCreated />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/mtd-teams"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <MTDTeams />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/closed-won-monthly"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <ClosedWonMonthly />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/marketing"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <MarketingReports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/marketing-funnel"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <MarketingFunnelReports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/product-revenue"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <ProductRevenueReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/revenue-trend"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <RevenueTrendReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/stuck-opportunities"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes', 'RFP']}
                      >
                        <StuckOpportunities />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/exec-sales-dashboard"
                    element={
                      <ProtectedRoute requireAdmin>
                        <ExecSalesDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rfps"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'RFP', 'Orders']}>
                        <Rfps />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rfps/:id"
                    element={
                      <ProtectedRoute allowedUserTypes={['User', 'RFP', 'Orders']}>
                        <RfpDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute allowedUserTypes={['Super Admin', 'Admin', 'Marketing']}>
                        <Analytics />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/where-in-the-world"
                    element={
                      <ProtectedRoute
                        allowedUserTypes={[
                          'Super Admin',
                          'Admin',
                          'User',
                          'Marketing',
                          'Orders',
                          'Quotes',
                          'RFP',
                        ]}
                      >
                        <WhereInTheWorld />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics/lead-sources"
                    element={
                      <ProtectedRoute allowedUserTypes={['Super Admin', 'Admin', 'Marketing']}>
                        <LeadSourceAnalysis />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics/sales-forecast"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesForecast />
                      </ProtectedRoute>
                    }
                  />
                  {/* Sales Trackers - QuickBooks billings actuals. Admin only:
                      company-wide revenue and a rep league table. When reps get
                      their own sheet it must be a separate session-scoped route,
                      never these with a widened guard. */}
                  <Route
                    path="/sales-trackers"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesTrackersOverview />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/products"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesTrackersProducts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/my-sales"
                    element={
                      // Reps, plus BDRs, who are a separate user type and would not be
                      // covered by 'User'. Admins are admitted automatically by
                      // allowedUserTypes, so View As lands here too.
                      <ProtectedRoute allowedUserTypes={['User', 'BDR']}>
                        <SalesTrackersMySales />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/reps"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesTrackersReps />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/reps/:ident"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesTrackersRepDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/forecast"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesTrackersWeeklyForecast />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/targets"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesTrackersTargets />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/transactions"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesTrackersTransactions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/my-transactions"
                    element={
                      // A rep's own lines, from the session-keyed endpoint. Same component,
                      // never the admin route with a widened guard: that one takes the rep
                      // from the URL, so widening it would let one rep read another's.
                      <ProtectedRoute allowedUserTypes={['User', 'BDR']}>
                        <SalesTrackersTransactions selfService />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/upload"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesTrackersUpload />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-trackers/attribution"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SalesTrackersAttribution />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/mass-upload"
                    element={
                      <ProtectedRoute requireAdmin>
                        <MassUpload />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/help" element={<Help />} />
                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute requireAdmin>
                        <Users />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/enhancement-tracker"
                    element={
                      <ProtectedRoute requireAdmin>
                        <EnhancementTracker />
                      </ProtectedRoute>
                    }
                  />

                  {/* HR module — open to all authenticated staff. Row-level access
                      is enforced server-side (self / your team / HR admin); the
                      directory, profile and org chart self-scope. Department and
                      company-document management remain HR-admin (also enforced
                      on the API). */}
                  <Route
                    path="/hr"
                    element={
                      <ProtectedRoute allowedUserTypes={[...HR_WORKSPACE_TYPES]}>
                        <HrHome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hr/me"
                    element={
                      <ProtectedRoute allowedUserTypes={[...HR_WORKSPACE_TYPES]}>
                        <MyProfileRedirect />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hr/employees"
                    element={
                      <ProtectedRoute allowedUserTypes={[...HR_WORKSPACE_TYPES]}>
                        <EmployeesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hr/employees/:id"
                    element={
                      <ProtectedRoute allowedUserTypes={[...HR_WORKSPACE_TYPES]}>
                        <EmployeeDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hr/org-chart"
                    element={
                      <ProtectedRoute allowedUserTypes={[...HR_WORKSPACE_TYPES]}>
                        <OrgChartPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* HR-admin-only pages: route open to authenticated staff, but
                      guarded in-page by HR-admin (so the hr@ role mailbox, an
                      'Employee' user type, can reach them) and enforced on the API. */}
                  <Route
                    path="/hr/departments"
                    element={
                      <ProtectedRoute allowedUserTypes={[...HR_WORKSPACE_TYPES]}>
                        <RequireHrAdmin>
                          <DepartmentsPage />
                        </RequireHrAdmin>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hr/company-documents"
                    element={
                      <ProtectedRoute allowedUserTypes={[...HR_WORKSPACE_TYPES]}>
                        <RequireHrAdmin>
                          <CompanyDocumentsPage />
                        </RequireHrAdmin>
                      </ProtectedRoute>
                    }
                  />
                  {/* Where in the World — Super Admin only (not HR admins). */}
                  <Route
                    path="/hr/where-in-the-world"
                    element={
                      <ProtectedRoute requireSuperAdmin>
                        <EmployeeWorldMap />
                      </ProtectedRoute>
                    }
                  />
                  {/* Time off — self-service for any authenticated user */}
                  <Route
                    path="/hr/time-off"
                    element={
                      <ProtectedRoute allowedUserTypes={[...HR_WORKSPACE_TYPES]}>
                        <TimeOffPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hr/leave-approvals"
                    element={
                      <ProtectedRoute allowedUserTypes={[...HR_WORKSPACE_TYPES]}>
                        <LeaveApprovalsPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Pipedrive routes - separate layout, restricted by user type */}
                <Route
                  element={
                    <ProtectedRoute allowedUserTypes={['User', 'Marketing', 'Orders', 'Quotes']}>
                      <PipedriveLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/pipedrive" element={<PipedriveDashboard />} />
                  <Route path="/pipedrive/organizations" element={<PipedriveOrganizations />} />
                  <Route
                    path="/pipedrive/organizations/:id"
                    element={<PipedriveOrganizationDetail />}
                  />
                  <Route path="/pipedrive/people" element={<PipedrivePeople />} />
                  <Route path="/pipedrive/people/:id" element={<PipedrivePersonDetail />} />
                  <Route path="/pipedrive/deals" element={<PipedriveDeals />} />
                  <Route path="/pipedrive/deals/:id" element={<PipedriveDealDetail />} />
                  <Route path="/pipedrive/activities" element={<PipedriveActivities />} />
                  <Route path="/pipedrive/activities/:id" element={<PipedriveActivityDetail />} />
                  <Route path="/pipedrive/notes" element={<PipedriveNotes />} />
                  <Route path="/pipedrive/notes/:id" element={<PipedriveNoteDetail />} />
                  <Route path="/pipedrive/leads" element={<PipedriveLeads />} />
                  <Route path="/pipedrive/products" element={<PipedriveProducts />} />
                  <Route path="/pipedrive/files" element={<PipedriveFiles />} />
                </Route>
              </Routes>
              <Toaster />
            </BrowserRouter>
          </CallProvider>
        </ErrorBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </UserProvider>
  )
}

export default App
